// ============================================
// ChatInterface.jsx - Main Chat Component
// ============================================
// Central chat area with messages and input
// Features: Welcome screen, messages, typing indicator, input

import React, { useState, useRef, useEffect } from 'react';
import {
  Menu,
  PanelRight,
  Copy,
  Check,
  Paperclip,
  Send,
  ChevronRight,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  MoreHorizontal,
  PenLine,
  Lightbulb,
  BarChart3,
  Palette,
} from 'lucide-react';
import GhostLogo from './GhostLogo';

// ============================================
// TYPING INDICATOR
// ============================================
function TypingIndicator() {
  return (
    <div className="typing-indicator">
      <span></span>
      <span></span>
      <span></span>
    </div>
  );
}

// ============================================
// MESSAGE COMPONENT
// ============================================
function Message({ message, onCopy, onRegenerate, onFeedback }) {
  const [copied, setCopied] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`message ${message.role}`}>
      <div className="message-avatar">
        {message.role === 'assistant' ? (
          <GhostLogo size={32} />
        ) : (
          <div className="user-avatar">You</div>
        )}
      </div>

      <div className="message-content">
        <div className="message-header">
          <span className="message-role">
            {message.role === 'assistant' ? 'GhostInk' : 'You'}
          </span>
          <span className="message-time">{message.timestamp}</span>
        </div>

        <div className="message-text">{message.content}</div>

        {message.role === 'assistant' && (
          <div className="message-actions">
            <button
              className="message-action-btn"
              onClick={handleCopy}
              title={copied ? 'Copied!' : 'Copy'}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
            <button
              className="message-action-btn"
              onClick={() => onRegenerate?.(message.id)}
              title="Regenerate"
            >
              <RefreshCw size={14} />
            </button>
            <button
              className="message-action-btn"
              onClick={() => onFeedback?.(message.id, 'up')}
              title="Good response"
            >
              <ThumbsUp size={14} />
            </button>
            <button
              className="message-action-btn"
              onClick={() => onFeedback?.(message.id, 'down')}
              title="Bad response"
            >
              <ThumbsDown size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// WELCOME SCREEN
// ============================================
function WelcomeScreen({ onSuggestionClick }) {
  const suggestions = [
    { icon: <PenLine size={18} />, text: 'Help me write a professional email' },
    { icon: <Lightbulb size={18} />, text: 'Explain a complex concept simply' },
    { icon: <BarChart3 size={18} />, text: 'Analyze data and create insights' },
    { icon: <Palette size={18} />, text: 'Generate creative content ideas' },
  ];

  return (
    <div className="welcome-screen">
      <div className="welcome-logo">
        <GhostLogo size={72} animated />
      </div>
      <h1 className="welcome-title">GhostInk AI</h1>
      <p className="welcome-subtitle">
        Your intelligent writing companion. Ask anything, create anything.
      </p>
      <div className="suggestions-grid">
        {suggestions.map((suggestion, idx) => (
          <button
            key={idx}
            className="suggestion-card"
            onClick={() => onSuggestionClick(suggestion.text)}
          >
            <span className="suggestion-icon">{suggestion.icon}</span>
            <span className="suggestion-text">{suggestion.text}</span>
            <ChevronRight size={16} className="suggestion-arrow" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================
// CHAT INPUT
// ============================================
function ChatInput({ value, onChange, onSend, onAttach, isLoading, placeholder }) {
  const textareaRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !isLoading) {
        onSend();
      }
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }, [value]);

  return (
    <div className="chat-input-wrapper">
      <div className="chat-input-container">
        <div className="chat-input">
          <button
            className="input-btn"
            onClick={onAttach}
            title="Attach file"
          >
            <Paperclip size={18} />
          </button>

          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || 'Message GhostInk...'}
            rows={1}
            disabled={isLoading}
          />

          <button
            className={`input-btn send ${value.trim() ? 'active' : ''}`}
            onClick={onSend}
            disabled={!value.trim() || isLoading}
          >
            <Send size={18} />
          </button>
        </div>

        <div className="chat-input-footer">
          <span>GhostInk may produce inaccurate information. Verify important details.</span>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MAIN CHAT INTERFACE
// ============================================
export default function ChatInterface({
  messages = [],
  isLoading = false,
  input = '',
  onInputChange,
  onSend,
  onAttach,
  onSuggestionClick,
  onRegenerate,
  onFeedback,
  activeSession,
  onToggleLeftSidebar,
  onToggleRightSidebar,
}) {
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  return (
    <main className="gi-main-content">
      {/* Header */}
      <header className="main-header">
        <div className="header-left">
          <button className="header-btn" onClick={onToggleLeftSidebar}>
            <Menu size={20} />
          </button>
        </div>

        <span className="header-title">
          {activeSession?.title || 'GhostInk AI'}
        </span>

        <div className="header-right">
          <button className="header-btn" onClick={onToggleRightSidebar}>
            <PanelRight size={20} />
          </button>
        </div>
      </header>

      {/* Chat Container */}
      <div className="chat-container">
        {messages.length === 0 ? (
          <WelcomeScreen onSuggestionClick={onSuggestionClick} />
        ) : (
          <div className="messages-container">
            {messages.map((message) => (
              <Message
                key={message.id}
                message={message}
                onRegenerate={onRegenerate}
                onFeedback={onFeedback}
              />
            ))}

            {isLoading && (
              <div className="message assistant">
                <div className="message-avatar">
                  <GhostLogo size={32} />
                </div>
                <div className="message-content">
                  <div className="message-header">
                    <span className="message-role">GhostInk</span>
                  </div>
                  <TypingIndicator />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <ChatInput
        value={input}
        onChange={onInputChange}
        onSend={onSend}
        onAttach={onAttach}
        isLoading={isLoading}
      />
    </main>
  );
}
